import { AxiosError } from 'axios';
import { postRequest, postRequestWithAuth, getRequestWithAuth } from '../api';
import { setSession, setUser, User } from '../session';

export type { User };

export interface OTPRequestPayload {
  email: string;
}

export interface OTPVerifyPayload {
  email: string;
  code: string;
}

export interface OTPResponse {
  success: boolean;
  message: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
}

/**
 * Request OTP code to be sent to email
 */
export const requestOTP = async (payload: OTPRequestPayload): Promise<OTPResponse> => {
  return await postRequest('/api/v1/auth/otp/request', payload, {
    headers: { 'X-App-Source': 'shopify' },
  });
};

/**
 * Verify OTP code and get JWT token
 */
export const verifyOTP = async (payload: OTPVerifyPayload): Promise<User> => {
  const response: AuthResponse = await postRequest('/api/v1/auth/otp/verify', payload, {
    headers: { 'X-App-Source': 'shopify' },
  });

  // Store session
  setSession(response.access_token, response.token_type);

  // Fetch and store user
  const user = await getRequestWithAuth('/api/v1/users/me');
  setUser(user);

  return user;
};

/**
 * Parse OTP auth error responses into user-friendly messages
 */
export const getOTPErrorMessage = (error: AxiosError<{ detail?: string }>): string => {
  const detail = error.response?.data?.detail;

  if (typeof detail === 'string') {
    // Pass through user-friendly error messages from backend
    if (
      detail.includes('wait') ||
      detail.includes('attempt') ||
      detail.includes('expired') ||
      detail.includes('Invalid')
    ) {
      return detail;
    }
  }

  return 'Something went wrong. Please try again.';
};

/**
 * Logout - calls backend to invalidate session
 */
export const logout = async (): Promise<void> => {
  await postRequestWithAuth('/api/v1/auth/jwt/logout', {});
};
